import type { Option } from '@/legacy/types';
import { markOptionsAsNonTranslatable } from '@/shared/utils/markOptionsAsNonTranslatable';

import type { AvailableDelegationsResponseDTO } from '../types';

export type AvailableDelegations = {
	/**
	 * The user may NOT act on this company as a whole — they must narrow the session down to at
	 * least one delegation. `false` means the list below is everything the company has and an
	 * empty selection is a legitimate "all delegations".
	 */
	mandatory: boolean;
	options: Option[];
};

// Delegation ids travel as numbers everywhere else in the session (`switch-credentials` sends
// `delegation: number[]`), so a value that isn't numeric could never be submitted — drop it
// instead of letting a NaN reach the form.
const toDelegationId = (value: string | number): number | null => {
	const id = typeof value === 'number' ? value : Number(value);

	return Number.isFinite(id) ? id : null;
};

export const mapAvailableDelegations = (
	dto: AvailableDelegationsResponseDTO,
): AvailableDelegations => {
	const options = (dto.delegations ?? []).flatMap<Option>(({ label, value }) => {
		const id = toDelegationId(value);

		return id === null ? [] : [{ label: String(label), value: id }];
	});

	return {
		mandatory: Boolean(dto.mandatory),
		// Delegation names are user data — without this the legacy Select runs each label through
		// t() (see Select/primitives/Option.tsx), as useDelegationOptions already does.
		options: markOptionsAsNonTranslatable(options),
	};
};
